import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { getPaddle } from "../lib/paddle";

// Pricing tiers per spec v2.0 (2026-02-20)
const TIERS = [
  {
    id: "base",
    name: "Base",
    monthlyPrice: 1500, // cents
    annualPrice: 1275, // $12.75/mo
    hours: 20,
    topUpRate: 0.60,
    rolloverCap: 40,
    chatMessages: 100,
    chatUnlimited: false,
  },
  {
    id: "pro",
    name: "Pro",
    monthlyPrice: 3000,
    annualPrice: 2550, // $25.50/mo
    hours: 45,
    topUpRate: 0.52,
    rolloverCap: 90,
    chatMessages: null, // unlimited
    chatUnlimited: true,
  },
  {
    id: "studio",
    name: "Studio",
    monthlyPrice: 5500,
    annualPrice: 4675, // $46.75/mo
    hours: 90,
    topUpRate: 0.48,
    rolloverCap: 180,
    chatMessages: null,
    chatUnlimited: true,
  },
  {
    id: "elite",
    name: "Elite",
    monthlyPrice: 9000,
    annualPrice: 7650, // $76.50/mo
    hours: 150,
    topUpRate: 0.44,
    rolloverCap: 300,
    chatMessages: null,
    chatUnlimited: true,
  },
];

const PAYG_RATE = 0.90; // $/hr for non-subscribers
const PAYG_CHAT_LIMIT = 20; // messages/month for PAYG

function formatPrice(cents: number): string {
  const dollars = cents / 100;
  // Max 2 decimals, but show 0 if whole number
  if (dollars % 1 === 0) return `$${dollars.toFixed(0)}`;
  return `$${dollars.toFixed(2)}`;
}

function formatHours(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  const display = hours % 1 === 0 ? String(hours) : hours.toFixed(1);
  return `${display} hr${hours !== 1 ? "s" : ""}`;
}

export default function PricingPage() {
  const [searchParams] = useSearchParams();
  const [isAnnual, setIsAnnual] = useState(false);
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  
  // PAYG slider - check for ?topup=X param (in minutes)
  const topupParam = searchParams.get("topup");
  const initialMinutes = topupParam ? parseInt(topupParam, 10) : 60;
  const [paygMinutes, setPaygMinutes] = useState(initialMinutes || 60);
  const [buyingPayg, setBuyingPayg] = useState(false);

  // If topup param exists, scroll to PAYG section
  useEffect(() => {
    if (topupParam) {
      const el = document.getElementById("payg-section");
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
      }
    }
  }, [topupParam]);

  const paygHours = paygMinutes / 60;
  const paygPriceCents = Math.round(paygHours * PAYG_RATE * 100);

  async function handleSubscribe(tierId: string) {
    setBuyingId(tierId);
    setCheckoutError(null);
    try {
      const res = await apiFetch<{ transaction_id: string }>(
        "/billing/subscribe",
        {
          method: "POST",
          body: JSON.stringify({
            tier_id: tierId,
            billing_cycle: isAnnual ? "annual" : "monthly",
            success_url: window.location.origin + "/billing/success",
            cancel_url: window.location.origin + "/pricing",
          }),
        },
      );
      const paddle = await getPaddle();
      if (paddle) {
        paddle.Checkout.open({
          transactionId: res.transaction_id,
          settings: { successUrl: window.location.origin + "/billing/success" },
        });
      } else {
        setCheckoutError("Payment checkout is not available right now. Please try again later.");
      }
    } catch (err) {
      console.error("Subscribe failed:", err);
      setCheckoutError("Something went wrong. Please try again.");
    } finally {
      setBuyingId(null);
    }
  }

  async function handleBuyPayg() {
    if (paygMinutes < 1) return;
    setBuyingPayg(true);
    setCheckoutError(null);
    try {
      const res = await apiFetch<{ transaction_id: string }>(
        "/billing/checkout",
        {
          method: "POST",
          body: JSON.stringify({
            payg_minutes: paygMinutes,
            price_cents: paygPriceCents,
            success_url: window.location.origin + "/billing/success",
            cancel_url: window.location.origin + "/pricing",
          }),
        },
      );
      const paddle = await getPaddle();
      if (paddle) {
        paddle.Checkout.open({
          transactionId: res.transaction_id,
          settings: { successUrl: window.location.origin + "/billing/success" },
        });
      } else {
        setCheckoutError("Payment checkout is not available right now. Please try again later.");
      }
    } catch (err) {
      console.error("PAYG checkout failed:", err);
      setCheckoutError("Something went wrong. Please try again.");
    } finally {
      setBuyingPayg(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Free Testing Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
            <span className="inline-flex items-center gap-2">
              <span className="text-xl">🎉</span>
              <span className="font-semibold">Free during early access</span>
            </span>
            <span className="text-blue-100 text-sm sm:text-base">
              — No charges while we're in testing. <a href="/terms#early-access" className="underline hover:text-white">Fair use terms apply</a>.
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Simple, transparent pricing
          </h1>
          <p className="mt-3 text-lg text-gray-600 max-w-3xl mx-auto">
            Every other tool gives you Speaker 1 and Speaker 2.{" "}
            <span className="font-semibold text-gray-900">Echonour gives you the actual names</span>
            {" "}— then lets you chat with your entire podcast library using AI of your choice.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-3 rounded-full bg-gray-100 p-1">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                !isAnnual
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${
                isAnnual
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Annual
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                15% off
              </span>
            </button>
          </div>
        </div>

        {/* Checkout error */}
        {checkoutError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700">
            {checkoutError}
          </div>
        )}

        {/* Subscription Tiers */}
        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-12">
          {TIERS.map((tier) => {
            const price = isAnnual ? tier.annualPrice : tier.monthlyPrice;
            return (
              <div
                key={tier.id}
                className="relative flex flex-col rounded-2xl border border-gray-200 hover:border-gray-300 p-5 sm:p-6 bg-white"
              >

                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  {tier.name}
                </h2>

                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                    {formatPrice(price)}
                  </span>
                  <span className="text-gray-500">/mo</span>
                </div>
                
                {isAnnual && (
                  <p className="text-xs text-gray-400 mb-3">
                    {formatPrice(tier.annualPrice * 12)} billed annually
                  </p>
                )}

                <div className="flex-1 space-y-3 mt-4 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-blue-600 font-semibold">{tier.hours} hrs</span>
                    <span className="text-gray-500">per month</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                    {tier.chatUnlimited ? (
                      <span className="font-medium text-green-700">Unlimited AI chat</span>
                    ) : (
                      <span>{tier.chatMessages} chat messages/mo</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                    Top-up at ${tier.topUpRate.toFixed(2)}/hr
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                    Rollover up to {tier.rolloverCap} hrs
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                  </svg>
                  BYOK: use your own API key
                </div>

                <button
                  onClick={() => handleSubscribe(tier.id)}
                  disabled={buyingId !== null}
                  className="w-full rounded-lg py-2.5 text-sm font-medium transition border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {buyingId === tier.id ? "Opening..." : "Subscribe"}
                </button>
              </div>
            );
          })}
        </div>

        {/* PAYG Section */}
        <div 
          id="payg-section"
          className={`rounded-2xl border p-6 sm:p-8 bg-white ${
            topupParam ? "ring-2 ring-blue-500 border-blue-500" : "border-gray-200"
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Top-up Hours
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                No subscription? Pay ${PAYG_RATE.toFixed(2)}/hr · Subscribers pay their discounted rate
              </p>
            </div>
            {topupParam && (
              <div className="rounded-lg px-4 py-2 text-sm text-blue-700">
                You need {paygMinutes} minutes for this transcription
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Slider */}
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={5}
                max={2400}
                step={5}
                value={paygMinutes}
                onChange={(e) => setPaygMinutes(parseInt(e.target.value, 10))}
                className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-blue-600"
              />
              <div className="flex items-center gap-2 min-w-[120px]">
                <input
                  type="number"
                  value={paygMinutes}
                  onChange={(e) => setPaygMinutes(parseInt(e.target.value, 10) || 5)}
                  className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-center text-sm font-medium text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  min={5}
                  max={6000}
                />
                <span className="text-sm text-gray-500">min</span>
              </div>
            </div>

            {/* Quick picks */}
            <div className="flex flex-wrap gap-2">
              {[60, 120, 300, 600, 1200, 2400].map((mins) => (
                <button
                  key={mins}
                  onClick={() => setPaygMinutes(mins)}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                    paygMinutes === mins
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {formatHours(mins / 60)}
                </button>
              ))}
            </div>

            {/* Price and buy */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-100">
              <div>
                <span className="text-3xl font-bold text-gray-900">
                  {formatPrice(paygPriceCents)}
                </span>
                <span className="ml-2 text-sm text-gray-500">
                  for {formatHours(paygHours)} ({paygMinutes.toLocaleString()} min)
                </span>
              </div>

              <button
                onClick={handleBuyPayg}
                disabled={buyingPayg || buyingId !== null || paygMinutes < 5}
                className="rounded-lg bg-gray-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:bg-gray-400 transition"
              >
                {buyingPayg ? "Opening..." : "Buy hours"}
              </button>
            </div>

            {/* PAYG info */}
            <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600 space-y-2">
              <div>
                <strong className="text-gray-900">PAYG includes:</strong>{" "}
                {PAYG_CHAT_LIMIT} AI chat messages/month • Named speakers • RSS + YouTube
              </div>
              <div>
                <strong className="text-gray-900">Want more?</strong>{" "}
                Pro subscribers get unlimited chat + ${TIERS[1].topUpRate.toFixed(2)}/hr top-ups (42% off)
              </div>
            </div>
          </div>
        </div>

        {/* Feature Comparison Table */}
        <div className="mt-16 overflow-x-auto">
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-6">
            Compare plans
          </h3>
          <table className="w-full min-w-[600px] text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600"></th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">PAYG</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">Base</th>
                <th className="text-center py-3 px-2 font-medium text-gray-900">Pro</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">Studio</th>
                <th className="text-center py-3 px-2 font-medium text-gray-600">Elite</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="py-3 px-4 text-gray-700">Transcription</td>
                <td className="text-center py-3 px-2 text-gray-600">$0.90/hr</td>
                <td className="text-center py-3 px-2 text-gray-600">20 hrs/mo</td>
                <td className="text-center py-3 px-2 text-gray-900 font-medium">45 hrs/mo</td>
                <td className="text-center py-3 px-2 text-gray-600">90 hrs/mo</td>
                <td className="text-center py-3 px-2 text-gray-600">150 hrs/mo</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">Named speakers</td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">RSS + YouTube</td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">AI chat</td>
                <td className="text-center py-3 px-2 text-gray-600">20 msgs</td>
                <td className="text-center py-3 px-2 text-gray-600">100 msgs</td>
                <td className="text-center py-3 px-2 text-green-700 font-medium">Unlimited</td>
                <td className="text-center py-3 px-2 text-green-700 font-medium">Unlimited</td>
                <td className="text-center py-3 px-2 text-green-700 font-medium">Unlimited</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">Top-up rate</td>
                <td className="text-center py-3 px-2 text-gray-600">$0.90/hr</td>
                <td className="text-center py-3 px-2 text-gray-600">$0.60/hr</td>
                <td className="text-center py-3 px-2 text-gray-900 font-medium">$0.52/hr</td>
                <td className="text-center py-3 px-2 text-gray-600">$0.48/hr</td>
                <td className="text-center py-3 px-2 text-gray-600">$0.44/hr</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">BYOK (your API key)</td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
                <td className="text-center py-3 px-2"><span className="text-green-600">✓</span></td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-700">Annual option</td>
                <td className="text-center py-3 px-2 text-gray-400">—</td>
                <td className="text-center py-3 px-2 text-gray-600">$12.75/mo</td>
                <td className="text-center py-3 px-2 text-gray-900 font-medium">$25.50/mo</td>
                <td className="text-center py-3 px-2 text-gray-600">$46.75/mo</td>
                <td className="text-center py-3 px-2 text-gray-600">$76.50/mo</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* BYOK info */}
        <div className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-6 text-center">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">
            Bring Your Own Key (BYOK)
          </h4>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto">
            Add your own Anthropic, OpenAI, or Google API key in Settings.
            BYOK unlocks <strong>unlimited chat</strong> on any tier and lets you choose your model
            (Opus, GPT-4o, Gemini, etc.). Transcription always uses Echonour credits.
          </p>
        </div>

        {/* Research Units - Beta */}
        <div className="mt-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Research Units</h3>
            <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">
              Coming Soon
            </span>
          </div>
          
          <div className="rounded-xl border border-purple-200 bg-purple-50 p-6 mb-6">
            <p className="text-sm text-purple-800 text-center">
              🔬 <strong>Research Mode</strong> lets you search the web and scrape pages during chat.
              Currently in beta — pricing shown below is planned for launch.
              <br />
              <span className="text-purple-600">During early access, research features are free.</span>
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { units: 50, price: 5, perUnit: 0.10, label: "Starter" },
              { units: 200, price: 15, perUnit: 0.075, label: "Regular", savings: "25% off" },
              { units: 500, price: 30, perUnit: 0.06, label: "Power", savings: "40% off" },
            ].map((pack) => (
              <div
                key={pack.units}
                className="rounded-xl border border-gray-200 bg-white p-5 text-center"
              >
                <p className="text-sm font-medium text-gray-500 mb-1">{pack.label}</p>
                <p className="text-3xl font-bold text-gray-900">{pack.units}</p>
                <p className="text-sm text-gray-500 mb-3">units</p>
                <p className="text-xl font-semibold text-gray-900">${pack.price}</p>
                <p className="text-xs text-gray-400">
                  ${pack.perUnit.toFixed(3)}/unit
                  {pack.savings && (
                    <span className="ml-1 text-green-600">({pack.savings})</span>
                  )}
                </p>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 text-center mt-4">
            1 unit ≈ 1 web search or page scrape. Average research query uses 2-5 units.
            <br />
            Units never expire. BYOK users only pay for tool costs, not LLM tokens.
          </p>
        </div>

      </div>
    </div>
  );
}
